import {supabase} from '../config/supabase'

// New credit cost constants
const CREDIT_COSTS = {
  BROAD_SEARCH: 0.5,
  CUSTOM_SEARCH: 0.1,
  CONTENT_GENERATION: 0.0025,
  EMAIL_EXTRACTION: 0.0025
}

export class CreditService {
  // Get pricing for credits
  static getPricing(quantity) {
    // $1 per credit, no volume discount
    return {
      price: quantity * 1.00, // $1.00 per credit
      currency: 'usd'
    }
  }

  // Create a purchase record
  static async createPurchase(userId, quantity, amount) {
    try {
      console.log(`Creating purchase record: userId=${userId}, quantity=${quantity}, amount=${amount}`)
      
      const {data, error} = await supabase
        .from('credit_purchases_k9l8x3')
        .insert([
          {
            user_id: userId,
            credits_purchased: quantity,
            amount_paid: quantity,
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (error) {
        console.error('Error creating purchase:', error)
        throw error
      }

      console.log('Purchase record created:', data)
      return data[0]
    } catch (error) {
      console.error('Error creating purchase:', error)
      throw error
    }
  }

  // Complete a purchase after Stripe payment
  static async completePurchase(sessionId, userId, quantity) {
    try {
      console.log(`Completing purchase: sessionId=${sessionId}, userId=${userId}, quantity=${quantity}`)

      // Create a purchase record with status=completed
      const {data: purchaseData, error: purchaseError} = await supabase
        .from('credit_purchases_k9l8x3')
        .insert([
          {
            user_id: userId,
            credits_purchased: quantity,
            amount_paid: quantity * 1.00, // $1 per credit (KRT)
            created_at: new Date().toISOString(),
            completed_at: new Date().toISOString(),
            payment_status: 'completed'
          }
        ])
        .select()

      if (purchaseError) {
        console.error('Error creating purchase record:', purchaseError)
        throw purchaseError
      }

      console.log('Purchase record created:', purchaseData)

      // Get current user credits
      const {data: userData, error: userError} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits, id')
        .eq('user_id', userId)
        .single()

      console.log('Current user credits data:', userData)

      if (userError && userError.code !== 'PGRST116') { // PGRST116 means no rows found
        console.error('Error fetching user credits:', userError)
        throw userError
      }

      // Calculate new credit balance (ensure proper decimal precision)
      const currentCredits = Number(Number(userData?.credits || 0).toFixed(4))
      const newBalance = Number((currentCredits + quantity).toFixed(4))

      console.log(`Credit calculation: ${currentCredits} + ${quantity} = ${newBalance}`)

      // Update or insert user credits
      let creditUpdateResult
      if (userData) {
        // Update existing record
        const {data: updateData, error: updateError} = await supabase
          .from('user_credits_k9l8x3')
          .update({
            credits: newBalance,
            updated_at: new Date().toISOString()
          })
          .eq('id', userData.id)
          .select()

        if (updateError) {
          console.error('Error updating user credits:', updateError)
          throw updateError
        }

        console.log('Credits updated successfully:', updateData)
        creditUpdateResult = updateData
      } else {
        // Create new record
        const {data: insertData, error: insertError} = await supabase
          .from('user_credits_k9l8x3')
          .insert([
            {
              user_id: userId,
              credits: newBalance,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ])
          .select()

        if (insertError) {
          console.error('Error inserting user credits:', insertError)
          throw insertError
        }

        console.log('Credits created successfully:', insertData)
        creditUpdateResult = insertData
      }

      // Create a transaction record
      const {data: transactionData, error: transactionError} = await supabase
        .from('credit_transactions_k9l8x3')
        .insert([
          {
            user_id: userId,
            amount: quantity,
            transaction_type: 'purchase',
            description: `Purchased ${quantity} credits`,
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (transactionError) {
        console.error('Error creating transaction record:', transactionError)
        throw transactionError
      }

      console.log('Transaction recorded successfully:', transactionData)

      return {
        success: true,
        credits: newBalance,
        transaction: transactionData[0]
      }
    } catch (error) {
      console.error('Error completing purchase:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }

  // Use a credit for broad search (default when using spendCredit)
  static async spendCredit(userId, searchId = null) {
    try {
      console.log("Attempting to spend 0.5 credits (broad search) for user:", userId)

      // Check if user has sufficient credits
      const {data: userData, error: userError} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits, id')
        .eq('user_id', userId)
        .single()

      if (userError) {
        console.error("Error fetching user credits:", userError)
        throw userError
      }

      console.log("Current user credits:", userData?.credits)

      if (!userData || userData.credits < CREDIT_COSTS.BROAD_SEARCH) {
        console.log("Insufficient credits:", userData?.credits)
        return false // Not enough credits
      }

      // Calculate new balance (ensure proper decimal precision)
      const currentCredits = Number(Number(userData.credits).toFixed(4))
      const newBalance = Number((currentCredits - CREDIT_COSTS.BROAD_SEARCH).toFixed(4))

      console.log("Calculated new balance:", newBalance)

      // Update user credits
      const {error: updateError} = await supabase
        .from('user_credits_k9l8x3')
        .update({
          credits: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id)

      if (updateError) {
        console.error("Error updating user credits:", updateError)
        throw updateError
      }

      // Create a transaction record
      const {data: transactionData, error: transactionError} = await supabase
        .from('credit_transactions_k9l8x3')
        .insert([
          {
            user_id: userId,
            amount: -CREDIT_COSTS.BROAD_SEARCH,
            transaction_type: 'usage',
            description: 'broad_search',
            search_id: searchId, // May be null initially
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (transactionError) {
        console.error("Error creating transaction record:", transactionError)
        throw transactionError
      }

      console.log("Transaction recorded successfully:", transactionData)
      return transactionData[0]
    } catch (error) {
      console.error('Error using credit:', error)
      return false
    }
  }

  // Use a credit for custom search (0.1)
  static async spendCustomSearchCredit(userId, searchId = null) {
    try {
      console.log('Attempting to spend 0.1 credits (custom search) for user:', userId)

      // Check if user has sufficient credits
      const {data: userData, error: userError} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits, id')
        .eq('user_id', userId)
        .single()

      if (userError) {
        console.error('Error fetching user credits:', userError)
        throw userError
      }

      console.log('Current user credits:', userData?.credits)

      if (!userData || userData.credits < CREDIT_COSTS.CUSTOM_SEARCH) {
        console.log('Insufficient credits:', userData?.credits)
        return false // Not enough credits
      }

      // Calculate new balance (ensure proper decimal precision)
      const currentCredits = Number(Number(userData.credits).toFixed(4))
      const newBalance = Number((currentCredits - CREDIT_COSTS.CUSTOM_SEARCH).toFixed(4))

      console.log('Calculated new balance:', newBalance)

      // Update user credits
      const {error: updateError} = await supabase
        .from('user_credits_k9l8x3')
        .update({
          credits: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id)

      if (updateError) {
        console.error('Error updating user credits:', updateError)
        throw updateError
      }

      // Create a transaction record
      const {data: transactionData, error: transactionError} = await supabase
        .from('credit_transactions_k9l8x3')
        .insert([
          {
            user_id: userId,
            amount: -CREDIT_COSTS.CUSTOM_SEARCH,
            transaction_type: 'usage',
            description: 'custom_search',
            search_id: searchId,
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (transactionError) {
        console.error('Error creating transaction record:', transactionError)
        throw transactionError
      }

      console.log('Transaction recorded successfully:', transactionData)
      return transactionData[0]
    } catch (error) {
      console.error('Error using credit for custom search:', error)
      return false
    }
  }

  // Update a credit transaction with search ID
  static async updateCreditTransaction(userId, searchId) {
    try {
      // Find the most recent usage transaction without a search_id
      const {data: transactions, error: fetchError} = await supabase
        .from('credit_transactions_k9l8x3')
        .select('*')
        .eq('user_id', userId)
        .eq('transaction_type', 'usage')
        .is('search_id', null)
        .order('created_at', {ascending: false})
        .limit(1)

      if (fetchError) {
        throw fetchError
      }

      if (transactions && transactions.length > 0) {
        const transaction = transactions[0]
        
        // Update the transaction with the search ID
        const {error: updateError} = await supabase
          .from('credit_transactions_k9l8x3')
          .update({ search_id: searchId })
          .eq('id', transaction.id)

        if (updateError) {
          throw updateError
        }
      }

      return true
    } catch (error) {
      console.error('Error updating credit transaction:', error)
      return false
    }
  }

  // Use a fraction of credit (0.0025) for content generation
  static async spendFractionalCredit(userId, searchId = null) {
    try {
      console.log('Attempting to spend 0.0025 credits for user:', userId)

      // First check if user has sufficient credits
      const {data: userData, error: userError} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits, id')
        .eq('user_id', userId)
        .single()

      if (userError) {
        console.error('Error fetching user credits:', userError)
        throw userError
      }

      console.log('Current user credits:', userData?.credits)

      if (!userData || userData.credits < CREDIT_COSTS.CONTENT_GENERATION) {
        console.log('Insufficient credits:', userData?.credits)
        return false // Not enough credits
      }

      // Calculate new balance using fixed decimal places
      const currentCredits = Number(userData.credits)
      const deduction = CREDIT_COSTS.CONTENT_GENERATION

      // Use toFixed(4) to ensure precision, then convert back to number
      const newCreditBalance = Number((currentCredits - deduction).toFixed(4))

      console.log('Calculated new balance:', newCreditBalance)

      // Update the user credits with the precise decimal value
      const {error: updateError} = await supabase
        .from('user_credits_k9l8x3')
        .update({
          credits: newCreditBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id)

      if (updateError) {
        console.error('Error updating user credits:', updateError)
        throw updateError
      }

      // Create a transaction record with exact decimal value
      const {data: transactionData, error: transactionError} = await supabase
        .from('credit_transactions_k9l8x3')
        .insert([
          {
            user_id: userId,
            amount: -CREDIT_COSTS.CONTENT_GENERATION, // Exact decimal value
            transaction_type: 'usage',
            description: 'content_generation',
            search_id: searchId,
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (transactionError) {
        console.error('Error recording transaction:', transactionError)
        throw transactionError
      }

      console.log('Transaction recorded successfully:', transactionData)
      return true
    } catch (error) {
      console.error('Error using fractional credit:', error)
      return false
    }
  }

  // Use a fraction of credit (0.0025) for email extraction
  static async spendFractionalCreditForEmail(userId, searchId = null) {
    try {
      console.log('Attempting to spend 0.0025 credits for email extraction for user:', userId)

      // First check if user has sufficient credits
      const {data: userData, error: userError} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits, id')
        .eq('user_id', userId)
        .single()

      if (userError) {
        console.error('Error fetching user credits:', userError)
        throw userError
      }

      console.log('Current user credits:', userData?.credits)

      if (!userData || userData.credits < CREDIT_COSTS.EMAIL_EXTRACTION) {
        console.log('Insufficient credits:', userData?.credits)
        return false // Not enough credits
      }

      // Calculate new balance using fixed decimal places
      const currentCredits = Number(userData.credits)
      const deduction = CREDIT_COSTS.EMAIL_EXTRACTION

      // Use toFixed(4) to ensure precision, then convert back to number
      const newCreditBalance = Number((currentCredits - deduction).toFixed(4))

      console.log('Calculated new balance:', newCreditBalance)

      // Update the user credits with the precise decimal value
      const {error: updateError} = await supabase
        .from('user_credits_k9l8x3')
        .update({
          credits: newCreditBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id)

      if (updateError) {
        console.error('Error updating user credits:', updateError)
        throw updateError
      }

      // Create a transaction record with exact decimal value
      const {data: transactionData, error: transactionError} = await supabase
        .from('credit_transactions_k9l8x3')
        .insert([
          {
            user_id: userId,
            amount: -CREDIT_COSTS.EMAIL_EXTRACTION, // Exact decimal value
            transaction_type: 'usage',
            description: 'email_extraction',
            search_id: searchId,
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (transactionError) {
        console.error('Error recording transaction:', transactionError)
        throw transactionError
      }

      console.log('Transaction recorded successfully:', transactionData)
      return true
    } catch (error) {
      console.error('Error using fractional credit for email extraction:', error)
      return false
    }
  }

  // Spend credits for directory access
  static async spendCreditsForDirectory(userId, amount) {
    try {
      console.log(`Attempting to spend ${amount} credits for directory access for user:`, userId)

      // Check if user has sufficient credits
      const {data: userData, error: userError} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits, id')
        .eq('user_id', userId)
        .single()

      if (userError) {
        console.error("Error fetching user credits:", userError)
        throw userError
      }

      console.log("Current user credits:", userData?.credits)

      if (!userData || userData.credits < amount) {
        console.log("Insufficient credits:", userData?.credits)
        return false // Not enough credits
      }

      // Calculate new balance (ensure proper decimal precision)
      const currentCredits = Number(Number(userData.credits).toFixed(4))
      const newBalance = Number((currentCredits - amount).toFixed(4))

      console.log("Calculated new balance:", newBalance)

      // Update user credits
      const {error: updateError} = await supabase
        .from('user_credits_k9l8x3')
        .update({
          credits: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id)

      if (updateError) {
        console.error("Error updating user credits:", updateError)
        throw updateError
      }

      // Create a transaction record
      const {data: transactionData, error: transactionError} = await supabase
        .from('credit_transactions_k9l8x3')
        .insert([
          {
            user_id: userId,
            amount: -amount,
            transaction_type: 'usage',
            description: 'Link Directory Access',
            created_at: new Date().toISOString()
          }
        ])
        .select()

      if (transactionError) {
        console.error("Error creating transaction record:", transactionError)
        throw transactionError
      }

      console.log("Transaction recorded successfully:", transactionData)
      return true
    } catch (error) {
      console.error('Error spending credits for directory:', error)
      return false
    }
  }

  // Helper method to format credit display
  static formatCredits(credits) {
    return Number(Number(credits).toFixed(4))
  }

  // Get user's current credit balance
  static async getUserCredits(userId) {
    try {
      console.log("Getting credits for user:", userId)
      
      const {data, error} = await supabase
        .from('user_credits_k9l8x3')
        .select('credits')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error("Error fetching credits:", error)
        throw error
      }

      // Format credits to 4 decimal places for new micro-costs
      const formattedCredits = this.formatCredits(data?.credits || 0)
      console.log("Formatted credits:", formattedCredits)
      return formattedCredits
    } catch (error) {
      console.error('Error fetching user credits:', error)
      return 0
    }
  }

  // Get credit transaction history with pagination
  static async getCreditTransactions(userId, page = 1, limit = 15) {
    try {
      console.log("Getting credit transactions for user:", userId, "page:", page, "limit:", limit)
      
      const offset = (page - 1) * limit
      
      const {data, error} = await supabase
        .from('credit_transactions_k9l8x3')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', {ascending: false})
        .range(offset, offset + limit - 1)

      if (error) {
        console.error("Error fetching transactions:", error)
        throw error
      }

      console.log("Credit transactions found:", data?.length || 0)
      return data || []
    } catch (error) {
      console.error('Error fetching credit transactions:', error)
      return []
    }
  }

  // Get total count of credit transactions for pagination
  static async getCreditTransactionsCount(userId) {
    try {
      console.log("Getting credit transaction count for user:", userId)
      
      const {count, error} = await supabase
        .from('credit_transactions_k9l8x3')
        .select('*', {count: 'exact', head: true})
        .eq('user_id', userId)

      if (error) {
        console.error("Error fetching transaction count:", error)
        throw error
      }

      console.log("Total credit transactions:", count)
      return count || 0
    } catch (error) {
      console.error('Error fetching credit transaction count:', error)
      return 0
    }
  }
}
